<template>
    <div class="col-12 mx-auto">
        <div class="card">
            <div class="card-header" v-if="showSettingsBtn">
                <kendo-datasource ref="paricipationTypeDatasource"
                                  :transport="paricipationTypeTransportSettings"
                                  :server-filtering="true"
                                  :server-paging="false"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="paricipationTypeSchemaModel"
                                  :error="dataSourceError"
                                  :requestEnd="datasorceRequestEnd"
                                  :typeof="'odata-v4'"></kendo-datasource>

                <button type="button"
                        class="btn btn-primary btn-lg"
                        v-if="showNewButton && hasUpdateRights"
                        @click="createBtnClick">
                    {{$t('buttons.create.btnText')}}
                </button>
                <i class="fas fa-cogs pointer fa-2x float-right text-primary"
                   v-if="showSettingsBtn"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @deleteGridSettings="deleteGridSettings"
                                    @userSettingsChanged="userSettingsChanged">
                </grid-user-settings>
            </div>

            <div class="btn-toolbar justify-content-between mt-3" role="toolbar">
                <div class="form-inline col-lg-6 col-md-12">
                    <div class="col-3">
                        <button class="btn btn-secondary" @click.stop="filterByParticipationType">
                            <i class="fas fa-filter"></i>
                            {{ $t('buttons.filter.btnText') }}
                        </button>
                    </div>
                    <div class="col-9">
                        <kendo-dropdownlist v-model="participationType"
                                            :data-source-ref="'paricipationTypeDatasource'"
                                            :data-text-field="'text'"
                                            :data-value-field="'text'"
                                            :filter="'contains'"
                                            :optionLabel="$t('choose')"
                                            style="width:100%"></kendo-dropdownlist>
                    </div>
                </div>

                <div class="input-group col-lg-6 col-md-12">
                    <input v-model="searchQuery"
                           v-on:keyup.enter.stop="massSearch"
                           v-on:keyup.esc.stop="clearFilters"
                           type="text"
                           class="form-control"
                           aria-label="Mass search input"
                           aria-describedby="massSearch" />
                    <button class="btn btn-secondary" @click.stop="massSearch">
                        <i class="fas fa-search"></i>
                        {{ $t('buttons.search.btnText') }}
                    </button>
                    <button class="btn btn-secondary" @click.stop="clearFilters">
                        <i class="fas fa-times"></i>
                        {{ $t('buttons.clearFilters.btnText') }}
                    </button>
                </div>
            </div>

            <div class="card-body" id="grid">
                <kendo-datasource ref="datasource"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size="gridOptions.pageable.size"
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError"
                                  :requestEnd="datasorceRequestEnd"></kendo-datasource>


                <kendo-grid ref="grid"
                            :data-source-ref="'datasource'"
                            :navigatable="gridOptions.navigatable"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :toolbar="toolbar"
                            :excel="gridOptions.excel"
                            :pdf="gridOptions.pdf"
                            v-on:detailinit="detailInit"
                            :data-bound="dataBound">
                    <kendo-grid-column :field="'id'" :title="$t('registers.events.participants.gridHeaders.id')" :width="100" :hidden="true"></kendo-grid-column>
                    <kendo-grid-column v-if="showBtns && hasUpdateRights"
                                       field="btnsColumn"
                                       :title="$t('actions')"
                                       :sortable="false"
                                       :filterable="false"
                                       :template="btnsTemplate"
                                       :width="150">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventName'"
                                       :title="$t('registers.events.participants.gridHeaders.eventName')"
                                       :width="200"
                                       :expandable="true"></kendo-grid-column>
                    <kendo-grid-column :field="'participationType'"
                                       :title="$t('registers.events.participants.gridHeaders.participationType')"
                                       :width="200"
                                       :hidden="isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'participationTypeEn'"
                                       :title="$t('registers.events.participants.gridHeaders.participationType')"
                                       :width="200"
                                       :hidden="!isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'titleDegree'" :title="$t('registers.events.participants.gridHeaders.titleDegree')" :width="150"></kendo-grid-column>
                    <kendo-grid-column :field="'titleDegreeEn'" :title="$t('registers.events.participants.gridHeaders.titleDegree')" :width="150" :hidden="!isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'fullName'" :title="$t('registers.events.participants.gridHeaders.name')" :width="300"></kendo-grid-column>
                    <kendo-grid-column :field="'fullNameTransliterated'" :title="$t('person.gridHeaders.nameTransliterated')" :sortable="false"
                                       :filterable="false" :width="300"></kendo-grid-column>
                    <kendo-grid-column :field="'identificationType'" :title="$t('registers.events.participants.gridHeaders.identificationType')" :width="100"></kendo-grid-column>
                    <kendo-grid-column :field="'identificationNumber'" :title="$t('registers.events.participants.gridHeaders.identificationNumber')" :width="150"></kendo-grid-column>
                    <kendo-grid-column :field="'email'" :title="$t('registers.events.participants.gridHeaders.email')" :width="150"></kendo-grid-column>
                    <kendo-grid-column :field="'position'" :title="$t('registers.events.participants.gridHeaders.position')" :width="150" :hidden="isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'positionEn'" :title="$t('registers.events.participants.gridHeaders.position')" :width="150" :hidden="!isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'positionStatistic'" :title="$t('registers.events.participants.gridHeaders.positionStatistic')" :width="150" :hidden="isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'positionStatisticEn'" :title="$t('registers.events.participants.gridHeaders.positionStatistic')" :width="150" :hidden="!isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'positionStr'" :title="$t('registers.events.participants.gridHeaders.positionFullName')" :width="150"></kendo-grid-column>
                    <kendo-grid-column :field="'educationDegree'" :title="$t('registers.events.participants.gridHeaders.educationDegree')" :width="200" :hidden="isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'educationDegreeEn'"
                                       :title="$t('registers.events.participants.gridHeaders.educationDegree')"
                                       :width="200"
                                       :hidden="!isEnVersion"></kendo-grid-column>
                    <!--<kendo-grid-column :field="'institutionNomenclature'"
                       :title="$t('registers.events.participants.gridHeaders.institutionNomenclature')"
                       :width="250"
                       :hidden="isEnVersion">
    </kendo-grid-column>
    <kendo-grid-column :field="'institutionNomenclatureEn'"
                       :title="$t('registers.events.participants.gridHeaders.institutionNomenclature')"
                       :width="250"
                       :hidden="!isEnVersion">
    </kendo-grid-column>-->
                    <kendo-grid-column :field="'institutionRegister'" :title="$t('registers.events.participants.gridHeaders.institutionNomenclature')" :width="250"></kendo-grid-column>
                    <kendo-grid-column :field="'institutionRegisterCity'" :title="$t('registers.events.participants.gridHeaders.institutionCity')" :width="150"></kendo-grid-column>
                    <kendo-grid-column :field="'institutionRegisterMigrationName'"
                                       :title="$t('registers.events.participants.gridHeaders.institutionMigrationName')"
                                       :width="250"></kendo-grid-column>
                    <kendo-grid-column :field="'relationTypeName'" :title="$t('registers.events.participants.gridHeaders.relationType')" :width="200" :hidden="isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'relationTypeNameEn'" :title="$t('registers.events.participants.gridHeaders.relationType')" :width="200" :hidden="!isEnVersion"></kendo-grid-column>
                    <kendo-grid-column :field="'createdAt'"
                                       :title="$t('registers.events.participants.gridHeaders.createdAt')"
                                       :width="150"
                                       :format="customDateTimeLongFormat"
                                       :hidden="!hasUpdateRights"></kendo-grid-column>
                    <kendo-grid-column :field="'updatedAt'"
                                       :title="$t('registers.events.participants.gridHeaders.updatedAt')"
                                       :width="150"
                                       :format="customDateTimeLongFormat"
                                       :hidden="!hasUpdateRights"></kendo-grid-column>
                    <kendo-grid-column :field="'eventStartDate'"
                                       :title="$t('registers.events.participants.gridHeaders.eventStartDate')"
                                       :width="150"
                                       :format="customDateTimeFormat"></kendo-grid-column>
                    <kendo-grid-column :field="'eventEndDate'"
                                       :title="$t('registers.events.participants.gridHeaders.eventEndDate')"
                                       :width="150"
                                       :format="customDateTimeFormat">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'moduleName'"
                                       :title="$t('registers.events.participants.gridHeaders.moduleName')"
                                       :width="150">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'isMainEventModule'"
                                       :title="$t('registers.events.participants.gridHeaders.isMainEventModule')"
                                       :width="150"
                                       :template="isMainEventModuleCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'cityOfCurrentAddress'"
                                       :title="$t('reportData.participants.cityOfCurrentAddress')"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'currentSettlement'"
                                       :title="$t('reportData.participants.currentSettlement')"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="isEnVersion">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'currentSettlementEn'"
                                       :title="$t('reportData.participants.currentSettlement')"
                                       :width="200"
                                       :lockable="true"
                                       :locked="false"
                                       :hidden="!isEnVersion">
                    </kendo-grid-column>
                </kendo-grid>
            </div>
        </div>

        <EditModal v-model="participantModel" :show="showEdit" @onClose="onEditClose" @onSave="onParticipantEdit"></EditModal>
    </div>
</template>

<script src="https://kendo.cdn.telerik.com/2019.2.619/js/pako_deflate.min.js"></script>

<script>
    import $ from 'jquery'; // това се изисква за да се вмъкват kendo ui компоненти. Виж метод parentIdDropDownEditor
    import Vue from 'vue';
    import JSZip from 'jszip'
    import GridUserSettings from "../../components/grid/UserSettings";
    import BtnsTemplate from "../../components/grid/StandardBtnsTemplate";
    import { Culture, KendoGridPageSize } from '../../models/enums';
    import EditModal from '../../components/participants/edit.modal';
    import participantService from '../../services/participant.service';
    import personalizationService from '../../services/personalization.service';
    import DatetimeCellDisplayTemplate from "../../components/grid/DatetimeCellDisplayTemplate";
    import { mapGetters } from 'vuex';

    /*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
    kendo.pdf.defineFont({
        "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
        "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
        "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
    });

    export default {
        name: 'AllAppearancesGrid',
        components: {
            'grid-user-settings': GridUserSettings,
            EditModal
        },
        created: function () {
            window.JSZip = JSZip
        },
        props: {
            showHideButton: {
                default: false,
                type: Boolean
            },
            showPinButton: {
                default: true,
                type: Boolean
            },
            showNewButton: {
                default: true,
                type: Boolean
            },
            showResizeIcon: {
                default: true,
                type: Boolean
            },
            size: {
                default: 6,
                type: Number
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            relatedEventId: {
                default: 0,
                type: Number
            },
            expandFirstMasterDetails: {
                default: false,
                type: Boolean
            },
            onlyParticipants: {
                default: false,
                type: Boolean
            },
            componentName: {
                default: 'AllAppearancesListGrid',
                type: String
            },
        },
        data() {
            return {
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                titleKey: 'sideMenu.allAppearances',
                participationType: '',
                searchQuery: '',
                showEdit: false,
                participantModel: null,
                schemaModelFields: {
                    id: { name: 'id', type: 'number', editable: false, nullable: false, visible: true },
                    registerEntityId: {
                        name: 'registerEntityId',
                        type: 'number',
                        editable: false,
                        nullable: false,
                        visible: true
                    },
                    registerFieldId: {
                        name: 'registerFieldId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    personId: { name: 'personId', type: 'number', editable: false, nullable: false, visible: true },
                    relationTypeId: {
                        name: 'relationTypeId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    participationCode: {
                        name: 'participationCode',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    participationType: {
                        name: 'participationType',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    participationTypeEn: {
                        name: 'participationTypeEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    relationTypeName: {
                        name: 'relationTypeName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    relationTypeNameEn: {
                        name: 'relationTypeNameEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    isRelationTypeForParticipation: {
                        name: 'isRelationTypeForParticipation',
                        type: 'boolean',
                        editable: false,
                        nullable: false,
                        visible: true
                    },
                    relationTypeCode: {
                        name: 'relationTypeCode',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventPersonTypeId: {
                        name: 'eventPersonTypeId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    registerEntityParticipantMigrationId: {
                        name: 'registerEntityParticipantMigrationId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    createdBy: { name: 'createdBy', type: 'string', editable: false, nullable: true, visible: true },
                    createdAt: { name: 'createdAt', type: 'date', editable: false, nullable: true, visible: true },
                    updatedBy: { name: 'updatedBy', type: 'string', editable: false, nullable: true, visible: true },
                    updatedAt: { name: 'updatedAt', type: 'date', editable: false, nullable: true, visible: true },
                    migrationData: {
                        name: 'migrationDataS',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    rejected: { name: 'rejected', type: 'boolean', editable: false, nullable: false, visible: true },
                    deleted: { name: 'deleted', type: 'boolean', editable: false, nullable: false, visible: true },
                    firstName: { name: 'firstName', type: 'string', editable: false, nullable: true, visible: true },
                    middleName: { name: 'middleName', type: 'string', editable: false, nullable: true, visible: true },
                    lastName: { name: 'lastName', type: 'string', editable: false, nullable: true, visible: true },
                    fullName: { name: 'fullName', type: 'string', editable: false, nullable: true, visible: true },
                    fullNameTransliterated: { name: 'fullNameTransliterated', type: 'string', editable: false, nullable: true, visible: true },
                    identificationType: {
                        name: 'identificationType',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    identificationNumber: {
                        name: 'identificationNumber',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    phone: { name: 'phone', type: 'string', editable: false, nullable: true, visible: true },
                    mobilePhone: { name: 'mobilePhone', type: 'string', editable: false, nullable: true, visible: true },
                    email: { name: 'email', type: 'string', editable: false, nullable: true, visible: true },
                    position: { name: 'position', type: 'string', editable: false, nullable: true, visible: true },
                    positionEn: { name: 'positionEn', type: 'string', editable: false, nullable: true, visible: true },
                    positionStatistic: {
                        name: 'positionStatistic',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    positionStatisticEn: {
                        name: 'positionStatisticEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    positionStr: {
                        name: 'positionStr',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    description: { name: 'description', type: 'string', editable: false, nullable: true, visible: true },
                    gender: { name: 'gender', type: 'string', editable: false, nullable: true, visible: true },
                    genderEn: { name: 'genderEn', type: 'string', editable: false, nullable: true, visible: true },
                    hasParent: { name: 'hasParent', type: 'boolean', editable: false, nullable: true, visible: true },
                    parentFirstName: {
                        name: 'parentFirstName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    parentMiddleName: {
                        name: 'parentMiddleName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    parentLastName: {
                        name: 'parentLastName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    parentFullName: {
                        name: 'parentFullName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    username: { name: 'username', type: 'string', editable: false, nullable: true, visible: true },
                    participantMigrationId: {
                        name: 'participantMigrationId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    lecturerMigrationId: {
                        name: 'lecturerMigrationId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    organizerMigrationId: {
                        name: 'organizerMigrationId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    educationDegree: {
                        name: 'educationDegree',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    educationDegreeEn: {
                        name: 'educationDegreeEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    titleDegree: { name: 'titleDegree', type: 'string', editable: false, nullable: true, visible: true },
                    titleDegreeEn: {
                        name: 'titleDegreeEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    institutionNomenclatureEn: {
                        name: 'institutionNomenclatureEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    institutionRegister: {
                        name: 'institutionRegister',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    institutionRegisterCity: {
                        name: 'institutionRegisterCity',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    institutionRegisterMigrationName: {
                        name: 'institutionRegisterMigrationName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    institutionNomenclature: {
                        name: 'institutionNomenclature',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    hasChanges: { name: 'hasChanges', type: 'boolean', editable: false, nullable: true, visible: true },
                    statusName: { name: 'statusName', type: 'string', editable: false, nullable: true, visible: true },
                    statusNameEn: {
                        name: 'statusNameEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventName: { name: 'eventName', type: 'string', editable: false, nullable: true, visible: true },
                    eventLocation: {
                        name: 'eventLocation',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventStartDate: {
                        name: 'eventStartDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventEndDate: {
                        name: 'eventEndDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventSubscriptionStartDate: {
                        name: 'eventSubscriptionStartDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventSubscriptionEndDate: {
                        name: 'eventSubscriptionEndDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventAudience: {
                        name: 'eventAudience',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    thematicArea: {
                        name: 'thematicArea',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    theme: { name: 'theme', type: 'string', editable: false, nullable: true, visible: true },
                    isMainEventModule: { name: 'isMainEventModule', type: 'boolean', editable: false, nullable: true, visible: true },
                    moduleName: { name: 'moduleName', type: 'string', editable: false, nullable: true, visible: true },
                    cityOfCurrentAddress: {
                        name: "cityOfCurrentAddress",
                        type: "string",
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    currentSettlement: {
                        name: "currentSettlement",
                        type: "string",
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                    currentSettlementEn: {
                        name: "currentSettlementEn",
                        type: "string",
                        editable: false,
                        nullable: true,
                        visible: true,
                    },
                },
                eventsSchemaModelFields: {
                    registerEntityId: {
                        name: 'registerEntityId',
                        type: 'number',
                        editable: false,
                        nullable: false,
                        visible: true
                    },
                    eventName: { name: 'eventName', type: 'string', editable: false, nullable: true, visible: true },
                    eventStartDate: {
                        name: 'eventStartDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventEndDate: { name: 'eventEndDate', type: 'date', editable: false, nullable: true, visible: true },
                    eventSubscriptionStartDate: {
                        name: 'eventSubscriptionStartDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventSubscriptionEndDate: {
                        name: 'eventSubscriptionEndDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    statusName: { name: 'statusName', type: 'string', editable: false, nullable: true, visible: true },
                    statusNameEn: {
                        name: 'statusNameEn',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventForm: { name: 'eventForm', type: 'string', editable: false, nullable: true, visible: true },
                    eventType: { name: 'eventType', type: 'string', editable: false, nullable: true, visible: true },
                    isEvent: { name: 'isEvent', type: 'boolean', editable: false, nullable: true, visible: true },
                    excludeFromReporting: {
                        name: 'excludeFromReporting',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    isPublic: { name: 'isPublic', type: 'boolean', editable: false, nullable: true, visible: true },
                    isLockedForEdit: {
                        name: 'isLockedForEdit',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    syncedWithMoodle: {
                        name: 'syncedWithMoodle',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    isCompleted: { name: 'isCompleted', type: 'boolean', editable: false, nullable: true, visible: true },
                    participantsMaxNum: {
                        name: 'participantsMaxNum',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventDailyAllowanceFrom: {
                        name: 'eventDailyAllowanceFrom',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventDailyAllowanceTo: {
                        name: 'eventDailyAllowanceTo',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventStayFrom: {
                        name: 'eventStayFrom',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventStayTo: { name: 'eventStayTo', type: 'date', editable: false, nullable: true, visible: true },
                    eventLocation: {
                        name: 'eventLocation',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventParticipantsCount: {
                        name: 'eventParticipantsCount',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventStatus: { name: 'eventStatus', type: 'string', editable: false, nullable: true, visible: true },
                    employeePosition: {
                        name: 'employeePosition',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventLegalArea: {
                        name: 'eventLegalArea',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventFundingType: {
                        name: 'eventFundingType',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventNote: { name: 'eventNote', type: 'string', editable: false, nullable: true, visible: true },
                    eventDirectorate: {
                        name: 'eventDirectorate',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventAudience: {
                        name: 'eventAudience',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    thematicArea: {
                        name: 'thematicArea',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    theme: { name: 'theme', type: 'string', editable: false, nullable: true, visible: true },
                    institutionOfEmployment: {
                        name: 'institutionOfEmployment',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventSort: { name: 'eventSort', type: 'string', editable: false, nullable: true, visible: true },
                    eventActivityDirection: {
                        name: 'eventActivityDirection',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventMoodleCategory: {
                        name: 'eventMoodleCategory',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventVenue: { name: 'eventVenue', type: 'string', editable: false, nullable: true, visible: true },
                    eventProjectName: {
                        name: 'eventProjectName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventOrganizerName: {
                        name: 'eventOrganizerName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventDescription: {
                        name: 'eventDescription',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventExpectedResults: {
                        name: 'eventExpectedResults',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventLearningGoals: {
                        name: 'eventLearningGoals',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventMigrationId: {
                        name: 'eventMigrationId',
                        type: 'number',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    deactivated: { name: 'deactivated', type: 'boolean', editable: false, nullable: true, visible: true },
                    deleted: { name: 'deleted', type: 'boolean', editable: false, nullable: true, visible: true },
                    archived: { name: 'archived', type: 'boolean', editable: false, nullable: true, visible: true },
                    useExtSubscriptionService: {
                        name: 'useExtSubscriptionService',
                        type: 'boolean',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    extSubscriptionService: {
                        name: 'extSubscriptionService',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    isMainEventModule: { name: 'isMainEventModule', type: 'boolean', editable: false, nullable: true, visible: true },
                    moduleName: { name: 'moduleName', type: 'string', editable: false, nullable: true, visible: true },
                },
                paricipationTypeSchemaModel: {
                    id: { name: 'id', from: 'idd', type: 'number', nullable: false },
                    text: { name: 'text', from: 'text', type: 'string', nullable: true },
                    textEn: { name: 'textEn', from: 'textEn', type: 'textEn', nullable: true },
                },
                paricipationTypeTransportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + `/api/participant/participationTypeDropdownModels`,
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.odata4ParameterMap
                },
                transportSettingsOData: {
                    read: {
                        url: this.$store.getters.baseUrl + `/api/participant/odataEventToPersonList`,
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.odata4ParameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    toolbar2: ['excel', 'pdf'],
                    excel: {
                        fileName: 'AllAppearances.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                    pdf: {
                        fileName: 'AllAppearances.pdf',
                        title: this.$t(this.titleKey),
                        subject: this.$t(this.titleKey),
                        creator: this.$store.userName,
                        allPages: false,
                        avoidLinks: true,
                        paperSize: 'A4',
                        margin: { top: '2cm', left: '1cm', right: '1cm', bottom: '1cm' },
                        landscape: true,
                        repeatHeaders: true,
                        scale: 0.6
                    }
                },
                working: false,
                showUserSettingsForm: false,
            }
        },
        methods: {
            dataBound(ev) {
                if (!this.customSettingsLoaded) {
                    this.customSettingsLoaded = true;
                    this.loadGridSettings();
                }

                if (this.expandFirstMasterDetails) {
                    ev.sender.expandRow(ev.sender.tbody.find('tr.k-master-row').first())
                }
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            odata4ParameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    var options = kendo.data.transports["odata-v4"].parameterMap(options, operation);

                    return options;
                }
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    return kendo.stringify(options);
                }
            },
            eventsParameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    let newOptions = { eventsListSettings: null, request: options }
                    return kendo.stringify(newOptions);
                }
            },
            detailInit: function (e) {
                $('<div />').appendTo(e.detailCell).kendoGrid({
                    dataSource: {
                        transport: {
                            read: {
                                url: this.$store.getters.baseUrl + '/api/events/list',
                                dataType: 'json',
                                type: 'POST',
                                contentType: 'application/json',
                                beforeSend: this.beforeSend
                            },
                            parameterMap: this.eventsParameterMap
                        },
                        serverPaging: false,
                        serverSorting: false,
                        serverFiltering: false,
                        filter: { // това филтрира само елементите за дадена родителска категория
                            field: 'registerEntityId',
                            operator: 'eq',
                            value: e.data.registerEntityId
                        },
                        schema: {
                            model: {
                                id: 'id',
                                fields: this.eventsSchemaModelFields
                            },
                            total: 'total',
                            data: 'data'
                        },
                        error: this.dataSourceError,
                        requestEnd: this.datasorceRequestEnd
                    },
                    editable: false,
                    pageable: false,
                    scrollable: this.gridOptions.scrollable,
                    sortable: this.gridOptions.sortable,
                    toolbar: [],
                    dataBound: function (e) {
                        let editLink = e.sender.element.find(".k-grid-customEdit");
                        if (!editLink.hasClass('custom-edit-btn')) {
                            editLink.addClass("btn btn-sm btn-success custom-edit-btn")
                            editLink.prepend('<span data-toggle="tooltip" title="' + this.$t('buttons.edit.btnText') + '"><i class="fas fa-edit"></i></span>');
                        }
                    }.bind(this),
                    columns: this.getDetailColumns()
                })
            },
            getDetailColumns() {
                var cols = [
                    {
                        width: 100,
                        command: [
                            {
                            name: 'customEdit',
                            text: " ",
                            click: this.navigateToEventEdit
                            }
                        ]
                    },
                    {
                        field: 'registerEntityId',
                        title: this.$t('registers.events.gridHeaders.id'),
                        width: 100
                    },
                    {
                        field: 'eventName',
                        title: this.$t('registers.events.gridHeaders.name'),
                        width: 350
                    },
                    {
                        field: 'eventStartDate',
                        title: this.$t('registers.events.gridHeaders.eventStartDate'),
                        width: 150,
                        format: this.customDateTimeFormat,
                    },
                    {
                        field: 'eventEndDate',
                        title: this.$t('registers.events.gridHeaders.eventEndDate'),
                        width: 150,
                        format: this.customDateTimeFormat,
                    },
                    {
                        field: 'eventSubscriptionStartDate',
                        title: this.$t('registers.events.gridHeaders.eventSubscriptionStartDate'),
                        width: 150,
                        format: this.customDateTimeFormat,
                    },
                    {
                        field: 'eventSubscriptionEndDate',
                        title: this.$t('registers.events.gridHeaders.eventSubscriptionEndDate'),
                        width: 150,
                        format: this.customDateTimeFormat,
                    },
                    {
                        field: 'eventDailyAllowanceFrom',
                        title: this.$t('registers.events.gridHeaders.eventDailyAllowanceFrom'),
                        width: 150,
                        format: this.customDateTimeFormat,
                    },
                    {
                        field: 'eventDailyAllowanceTo',
                        title: this.$t('registers.events.gridHeaders.eventDailyAllowanceTo'),
                        width: 150,
                        format: this.customDateTimeFormat,
                    },
                    {
                        field: 'eventStayFrom',
                        title: this.$t('registers.events.gridHeaders.eventStayFrom'),
                        width: 150,
                        format: this.customDateTimeFormat,
                    },
                    {
                        field: 'eventStayTo',
                        title: this.$t('registers.events.gridHeaders.eventStayTo'),
                        width: 150,
                        format: this.customDateTimeFormat,
                    },
                    {
                        field: 'statusName',
                        title: this.$t('registers.events.gridHeaders.statusName'),
                        width: 150,
                        hidden: this.isEnVersion
                    },
                    {
                        field: 'statusNameEn',
                        title: this.$t('registers.events.gridHeaders.statusName'),
                        width: 150,
                        hidden: !this.isEnVersion
                    },
                    {
                        field: 'eventForm',
                        title: this.$t('registers.events.gridHeaders.eventForm'),
                        width: 150,
                    },
                    {
                        field: 'eventType',
                        title: this.$t('registers.events.gridHeaders.eventType'),
                        width: 150,
                    },
                    {
                        field: 'eventVenue',
                        title: this.$t('registers.events.gridHeaders.eventVenue'),
                        width: 150,
                    },
                    {
                        field: 'eventProjectName',
                        title: this.$t('registers.events.gridHeaders.eventProjectName'),
                        width: 150,
                    },
                    {
                        field: 'eventOrganizerName',
                        title: this.$t('registers.events.gridHeaders.eventOrganizerName'),
                        width: 150,
                    },
                    {
                        field: 'participantsMaxNum',
                        title: this.$t('registers.events.gridHeaders.participantsMaxNum'),
                        width: 150,
                    },
                    {
                        field: 'eventLocation',
                        title: this.$t('registers.events.gridHeaders.eventLocation'),
                        width: 150,
                    },
                    {
                        field: 'eventActivityDirection',
                        title: this.$t('registers.events.gridHeaders.eventActivityDirection'),
                        width: 150,
                    },
                    {
                        field: 'eventLegalArea',
                        title: this.$t('registers.events.gridHeaders.eventLegalArea'),
                        width: 150,
                    },
                    {
                        field: 'eventFundingType',
                        title: this.$t('registers.events.gridHeaders.eventFundingType'),
                        width: 150,
                    },
                    {
                        field: 'eventDirectorate',
                        title: this.$t('registers.events.gridHeaders.eventDirectorate'),
                        width: 150,
                    },
                    {
                        field: 'eventNote',
                        title: this.$t('registers.events.gridHeaders.eventNote'),
                        width: 150,
                    },
                    {
                        field: 'eventAudience',
                        title: this.$t('registers.events.gridHeaders.eventAudience'),
                        width: 150,
                    },
                    {
                        field: 'thematicArea',
                        title: this.$t('registers.events.gridHeaders.thematicArea'),
                        width: 150,
                    },
                    {
                        field: 'theme',
                        title: this.$t('registers.events.gridHeaders.theme'),
                        width: 150,
                    },
                    {
                        field: 'institutionOfEmployment',
                        title: this.$t('registers.events.gridHeaders.institutionOfEmployment'),
                        width: 150,
                    },
                    {
                        field: 'eventSort',
                        title: this.$t('registers.events.gridHeaders.eventSort'),
                        width: 150,
                    },
                    {
                        field: 'eventMoodleCategory',
                        title: this.$t('registers.events.gridHeaders.eventMoodleCategory'),
                        width: 150,
                    },
                    {
                        field: 'eventDescription',
                        title: this.$t('registers.events.gridHeaders.eventDescription'),
                        width: 150,
                    },
                    {
                        field: 'eventExpectedResults',
                        title: this.$t('registers.events.gridHeaders.eventExpectedResults'),
                        width: 150,
                    },
                    {
                        field: 'eventLearningGoals',
                        title: this.$t('registers.events.gridHeaders.eventLearningGoals'),
                        width: 150,
                    },
                    {
                        field: 'moduleName',
                        title: this.$t('registers.events.gridHeaders.moduleName'),
                        width: 150,
                    },
                    {
                        field: 'isMainEventModule',
                        title: this.$t('registers.events.gridHeaders.isMainEventModule'),
                        template: this.isMainEventModuleCellTemplate,
                        width: 150,
                    },
                ];


                if (!this.claims.event.update) {
                    cols.splice(0, 1);
                }

                return cols;
            },
            navigateToEventEdit(e) {
                e.preventDefault();

                var tr = $(e.currentTarget).closest("tr");
                var detailsGrid = tr.closest('.k-grid').data("kendoGrid");
                var dataItem = detailsGrid.dataItem(tr);

                let routeData = this.$router.resolve({ name: 'eventsEdit', params: { id: dataItem.registerEntityId } });
                window.open(routeData.href, '_blank');

            },
            navigateToEventDetails(e) {
                e.preventDefault();
                var grid = this.$refs.grid.kendoWidget();
                var dataItem = grid.dataItem($(e.currentTarget).closest("tr"));

                alert('"' + dataItem.registerEntityId + '" details.');
            },
            getGridOptions() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    return grid.getOptions();
                }
            },
            saveGridSettings() {
                const gridSettings = this.getGridOptions();

                if (gridSettings && grid) {
                    this.$refs.userSettings.setGridSettings(gridSettings);

                    personalizationService
                        .saveGridSettings(this.componentName, gridSettings)
                        .then(response => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: this.$helpers.errorColor(error),
                                time: this.$store.getters.notificationDuration
                            })
                        });
                }
            },
            loadGridSettings() {
                personalizationService
                    .loadGridSettings(this.componentName)
                    .then(response => {
                        try {
                            var options = personalizationService
                                .mergePersonGridSettings(this.getGridOptions(), response.data.settings);

                            let grid = this.$refs.grid.kendoWidget();
                            if (options && grid) {
                                grid.setOptions(options);

                                if (options.pageable.size != grid.dataSource.options.pageSize) {
                                    grid.dataSource.pageSize(options.pageable.size);
                                    grid.refresh();
                                }

                                const userSettingsEditor = this.$refs.userSettings;
                                if (userSettingsEditor) {
                                    userSettingsEditor.setGridSettings(grid.getOptions());
                                }
                            }

                            this.customSettingsLoaded = true;
                        } catch (e) {
                            this.customSettingsLoaded = true;
                        }

                    })
                    .catch(error => {
                        this.customSettingsLoaded = true;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            deleteGridSettings() {
                personalizationService
                    .delteGridSettings(this.componentName)
                    .then(response => {
                        this.$router.go();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();

                if (grid) {
                    try {
                        grid.setOptions(userSettings);
                        if (pageSizeChanged === true) {
                            if (userSettings.pageable.size == KendoGridPageSize.All) {
                                grid.dataSource.pageSize(grid.dataSource.total());
                            }
                            else {
                                grid.dataSource.pageSize(userSettings.pageable.size);
                            }
                            grid.refresh();
                        }
                    } catch (e) {
                        return;
                    }
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            resize(componentName, size) {
                this.$emit('resize', componentName, size, this.$refs.grid.kendoWidget())
            },
            hide(componentName) {
                this.$emit('hide', componentName)
            },
            pinned(componentName) {
                this.$emit('pinned', componentName)
            },
            unpinned(componentName) {
                this.$emit('unpinned', componentName)
            },
            datasorceRequestEnd(e) {
                if (e.type == "read" && e.response) {
                    // 	console.log(e.response[0]);
                    //alert(e.respondashboard-ctrlse[0].TaskID);
                }
            },
            datetimeDisplayTemplate(date, useShortFormat) {
                return {
                    template: Vue.component(DatetimeCellDisplayTemplate.name, DatetimeCellDisplayTemplate),
                    templateArgs: {
                        dateValue: date,
                        showShortDate: useShortFormat
                    },
                };
            },
            datetimeDisplayTemplateCreatedAt: function (e) {
                return this.datetimeDisplayTemplate(e.createdAt, false);
            },
            datetimeDisplayTemplateUpdatedAt: function (e) {
                return this.datetimeDisplayTemplate(e.updatedAt, false);
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        parentComponent: this.$refs.grid,
                        btns: [
                            {
                                name: "editNew",
                                text: "",
                                tooltip: this.$t('buttons.edit.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-edit",
                                show: this.hasUpdateRights,
                                clickHandler: this.editNewBtnClick
                            },
                            {
                                name: "accountDetailsInNewTab",
                                text: "",
                                tooltip: this.$t('buttons.accountDetailsInNewTab.btnText'),
                                class: 'btn btn-info',
                                icon: "fas fa-eye",
                                show: this.hasReadRights,
                                clickHandler: this.accountDetailsInNewTabBtnClick
                            },
                            {
                                name: "detailsInNewTab",
                                text: "",
                                tooltip: this.$t('buttons.detailsInNewTab.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-eye",
                                show: this.hasReadRights,
                                clickHandler: this.detailsInTewTabBtnClick
                            }
                        ]
                    }
                };
            },
            eventsBtnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        btns: this.hasUpdateRights
                            ? [
                                {
                                    name: "edit",
                                    text: "",
                                    tooltip: this.$t('buttons.edit.btnText'),
                                    class: 'btn btn-primary',
                                    icon: "fas fa-edit",
                                    show: true,
                                    clickHandler: this.editEventBtnClick
                                }]
                            : []
                    }
                };
            },
            editEventBtnClick(args) {

            },
            accountDetailsInNewTabBtnClick(args) {
                let routeData = this.$router.resolve({ name: 'account', params: { id: args.personId } })
                window.open(routeData.href, '_blank')
            },
            detailsInTewTabBtnClick(args) {
                let routeData = this.$router.resolve({ name: 'participantsDetails', params: { id: args.id } })
                window.open(routeData.href, '_blank')
            },
            editBtnClick(args) {
                this.$router.push('/participant/edit?id=' + args.id);

                //let routeData = this.$router.resolve({ name: 'eventParticipantEdit', query: { id: args.id } });
                //window.open(`${this.$store.getters.baseUrl}${routeData.href}`, '_blank');
            },
            refresh() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.read();
                }
            },
            filterByParticipationType() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    if (!this.participationType) {
                        this.clearFilters();
                    } else {
                        let filter = { logic: 'or', filters: [] };
                        filter.filters.push({
                            field: 'participationType',
                            operator: 'contains',
                            value: this.participationType
                        });

                        grid.dataSource.filter(filter);

                    }
                    this.refresh();
                }
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            editNewBtnClick(args) {
                if (!this.hasUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                this.showEditModal(args.id);
            },
            showEditModal(id) {
                participantService
                    .getById(id)
                    .then(response => {
                        let model = response.data.participant;
                        if (model) {
                            this.participantModel = model;
                            this.showEdit = true;
                        } else {
                            this.participantModel = null;
                            this.showEdit = false;
                        }
                    })
                    .catch(error => {
                        this.participantModel = null;
                        this.showEdit = false;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            onEditClose() {
                this.showEdit = false;
                this.participantModel = null;
            },
            onParticipantEdit(item) {
                this.refresh();
            },
            isMainEventModuleCellTemplate: function (dataItem) {
                let templateHtml =
                    '<span class="badge ' + (dataItem.isMainEventModule ? 'badge-success' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.isMainEventModule ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
        },
        mounted() {

        },
        computed: {
            ...mapGetters(['dateTimeFormat', 'dateTimeLongFormat', 'claims']),
            customDateTimeFormat() {
                return `{0: ${this.dateTimeFormat} }`;
            },
            customDateTimeLongFormat() {
                return `{0: ${this.dateTimeLongFormat} }`;
            },
            token: function () {
                return this.$store.getters.authToken;
            },
            userSettings() {
                let grid = this.$refs.grid.kendoWidget();
                return grid.getOptions();
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
            hasReadRights() {
                return this.claims.participant.read;
            },
            hasUpdateRights() {
                return this.claims.participant.update;
            },
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }]
            },
            transportSettings() {
                let urlSuffix = '';
                if (this.onlyParticipants) {
                    urlSuffix = 'eventToParticipantList'
                } else {
                    urlSuffix = 'eventToPersonList'
                }

                return {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/participant/' + urlSuffix,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                }
            }
        }
    }
</script>


<style>

    .grid-wrapper {
        overflow-x: auto;
    }

    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: 'DejaVu Sans', 'Arial', sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit,
    .k-grid-details,
    .k-grid-delete,
    .k-grid-pdf,
    .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }

    .k-grid td {
        white-space: nowrap;
        text-overflow: ellipsis;
    }
</style>
